import { useEffect, useMemo, useState } from "react";
import {
  type Character,
  type ContentRating,
  type GameCard,
  type HubContentKind,
  type Lorebook,
  type Preset,
  type PublishMetaOverride,
} from "@roqeet/core";
import { storage } from "../../state/storage.js";
import {
  publishCharacter,
  publishGameCard,
  publishLorebook,
  publishPreset,
} from "../../state/hub-actions.js";
import { Modal } from "./HubView.js";
import { useT } from "../../i18n/use-t.js";
import type { TKey } from "../../i18n/strings.js";

/**
 * 발행 다이얼로그 — 결정 #23/#29. 로컬 엔티티(캐릭터·로어북·프리셋·게임 카드)를 골라
 * 허브에 올린다. 메타(제목·태그·등급)는 엔티티에서 기본값을 끌어오고, 발행 폼에서
 * 다듬는다(override). 페이로드는 core 빌더가 교환 포맷(결정 #6)으로 직렬화한다.
 */

const KIND_OPTIONS: { kind: HubContentKind; labelKey: TKey }[] = [
  { kind: "character", labelKey: "comm.kind.character" },
  { kind: "game-card", labelKey: "comm.kind.game" },
  { kind: "lorebook", labelKey: "comm.kind.lorebook" },
  { kind: "preset", labelKey: "comm.kind.preset" },
];

interface PickItem {
  id: string;
  title: string;
  rating: ContentRating;
}

export function PublishDialog({
  onClose,
  onPublished,
}: {
  onClose: () => void;
  onPublished: (title: string) => void;
}) {
  const { t } = useT();
  const [kind, setKind] = useState<HubContentKind>("character");
  const [items, setItems] = useState<PickItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 발행 메타 폼.
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [rating, setRating] = useState<ContentRating>("unrated");
  const [version, setVersion] = useState("");
  const [license, setLicense] = useState("");
  const [sourceVisibility, setSourceVisibility] = useState<"public" | "hidden">("public");
  const [statsPrivacy, setStatsPrivacy] = useState<"public" | "private" | "uncollected">("public");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 종류가 바뀌면 그 종류의 로컬 엔티티를 나열한다.
  useEffect(() => {
    let live = true;
    setItems(null);
    setSelectedId(null);
    (async () => {
      const picks = await loadPicks(kind);
      if (live) setItems(picks);
    })();
    return () => {
      live = false;
    };
  }, [kind]);

  // 항목 선택 시 메타 기본값 채우기.
  useEffect(() => {
    if (!selectedId || !items) return;
    const item = items.find((i) => i.id === selectedId);
    if (!item) return;
    setTitle(item.title);
    setRating(item.rating);
  }, [selectedId, items]);

  const selected = useMemo(
    () => items?.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  const publish = async () => {
    if (!selectedId) {
      setError(t("publish.errSelectItem"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const override: PublishMetaOverride = {
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
        tags: tagsText
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        rating,
        ...(version.trim() ? { version: version.trim() } : {}),
        ...(license.trim() ? { license: license.trim() } : {}),
        sourceVisibility,
        statsPrivacy,
      };

      const listing = await publishSelected(kind, selectedId, override, t);
      onPublished(listing);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.startsWith("lint.comm.") ? t(msg as any) : msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t("publish.title")} onClose={onClose}>
      <div className="space-y-4">
        {/* 종류 */}
        <div>
          <Label>{t("publish.kind")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {KIND_OPTIONS.map((o) => (
              <button
                key={o.kind}
                className={[
                  "rounded-lg px-2.5 py-1 text-[11px] font-medium border transition duration-150 active:scale-95",
                  kind === o.kind
                    ? "bg-accent/15 border-accent text-accent-soft shadow-sm"
                    : "border-line text-ink-soft hover:border-ink-faint hover:bg-bg-raised/50",
                ].join(" ")}
                onClick={() => setKind(o.kind)}
              >
                {t(o.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* 항목 선택 */}
        <div>
          <Label>{t("publish.item")}</Label>
          {items === null ? (
            <p className="text-xs text-ink-faint">{t("common.loading")}</p>
          ) : items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-line px-3 py-6 text-center text-xs text-ink-faint">
              {t("publish.noItems")}
            </p>
          ) : (
            <select
              className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition cursor-pointer"
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(e.target.value || null)}
            >
              <option value="">{t("publish.selectPlaceholder")}</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.title}
                </option>
              ))}
            </select>
          )}
        </div>

        {selected && (
          <div className="space-y-3 pt-2 border-t border-line/40">
            <Field label={t("publish.fieldTitle")}>
              <input
                className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
              />
            </Field>

            <Field label={t("publish.fieldSummary")}>
              <input
                className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={t("publish.summaryPlaceholder")}
              />
            </Field>

            <Field label={t("publish.fieldDescription")}>
              <textarea
                className="h-24 w-full resize-none rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition leading-relaxed"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("publish.descriptionPlaceholder")}
              />
            </Field>

            <Field label={t("publish.fieldTags")}>
              <input
                className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder={t("publish.tagsPlaceholder")}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t("publish.fieldRating")}>
                <select
                  className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition cursor-pointer"
                  value={rating}
                  onChange={(e) => setRating(e.target.value as ContentRating)}
                >
                  <option value="unrated">{t("publish.ratingUnrated")}</option>
                  <option value="sfw">{t("publish.ratingSfw")}</option>
                  <option value="nsfw">{t("publish.ratingNsfw")}</option>
                </select>
              </Field>
              <Field label={t("publish.fieldVersion")}>
                <input
                  className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="1.0.0"
                />
              </Field>
            </div>

            <Field label={t("publish.fieldLicense")}>
              <input
                className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition"
                value={license}
                onChange={(e) => setLicense(e.target.value)}
                placeholder={t("publish.licensePlaceholder")}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3 pt-1">
              <Field label={t("publish.sourceVisibility")}>
                <select
                  className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition cursor-pointer"
                  value={sourceVisibility}
                  onChange={(e) => setSourceVisibility(e.target.value as "public" | "hidden")}
                >
                  <option value="public">{t("publish.sourcePublic")}</option>
                  <option value="hidden">{t("publish.sourceHidden")}</option>
                </select>
              </Field>
              <Field label={t("publish.statsPrivacy")}>
                <select
                  className="w-full rounded-xl border border-line bg-bg-soft px-3 py-2 text-xs text-ink focus:border-accent/60 outline-none transition cursor-pointer"
                  value={statsPrivacy}
                  onChange={(e) => setStatsPrivacy(e.target.value as "public" | "private" | "uncollected")}
                >
                  <option value="public">{t("publish.statsPublic")}</option>
                  <option value="private">{t("publish.statsPrivate")}</option>
                  <option value="uncollected">{t("publish.statsUncollected")}</option>
                </select>
              </Field>
            </div>

            {rating === "nsfw" && (
              <p className="rounded-xl bg-red-500/10 border border-red-500/20 px-3 py-2 text-[10px] text-red-400 leading-relaxed font-medium">
                ⚠️ {t("publish.nsfwWarning")}
              </p>
            )}

            {error && (
              <p className="text-xs font-semibold text-red-400 p-2 bg-red-500/10 rounded-lg border border-red-500/20">{error}</p>
            )}

            <button
              className="w-full rounded-xl bg-accent px-4 py-2.5 text-xs font-semibold text-white hover:bg-accent-soft hover:shadow-lg hover:shadow-accent/10 active:scale-[0.98] transition disabled:opacity-50 mt-4"
              onClick={() => void publish()}
              disabled={busy}
            >
              {busy ? t("publish.publishing") : t("publish.submit")}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** 종류별 로컬 엔티티를 발행 후보로 나열한다(소프트 삭제 제외). */
async function loadPicks(kind: HubContentKind): Promise<PickItem[]> {
  switch (kind) {
    case "character": {
      const list = (await storage.characters.list()) as Character[];
      return list.map((c) => ({ id: c.id, title: c.name, rating: c.rating ?? "unrated" }));
    }
    case "lorebook": {
      const list = (await storage.lorebooks.list()) as Lorebook[];
      return list.map((b) => ({ id: b.id, title: b.name, rating: "unrated" as ContentRating }));
    }
    case "preset": {
      const list = (await storage.presets.list()) as Preset[];
      return list.map((p) => ({ id: p.id, title: p.name, rating: "sfw" as ContentRating }));
    }
    case "game-card": {
      const list = (await storage.gameCards.list()) as GameCard[];
      return list.map((g) => ({
        id: g.id,
        title: g.definition.title,
        rating: g.rating ?? "unrated",
      }));
    }
    default:
      return [];
  }
}

/** 선택한 엔티티를 발행하고 발행물 제목을 반환한다. */
async function publishSelected(
  kind: HubContentKind,
  id: string,
  override: PublishMetaOverride,
  t: (key: TKey, params?: Record<string, string | number>) => string,
): Promise<string> {
  switch (kind) {
    case "character": {
      const c = await storage.characters.get(id);
      if (!c) throw new Error(t("publish.errCharacterNotFound"));
      const listing = await publishCharacter(c as Character, override);
      return listing.title;
    }
    case "lorebook": {
      const b = await storage.lorebooks.get(id);
      if (!b) throw new Error(t("publish.errLorebookNotFound"));
      const listing = await publishLorebook(b as Lorebook, override);
      return listing.title;
    }
    case "preset": {
      const p = await storage.presets.get(id);
      if (!p) throw new Error(t("publish.errPresetNotFound"));
      const listing = await publishPreset(p as Preset, override);
      return listing.title;
    }
    case "game-card": {
      const g = await storage.gameCards.get(id);
      if (!g) throw new Error(t("publish.errGameCardNotFound"));
      const listing = await publishGameCard(g as GameCard, override);
      return listing.title;
    }
    default:
      throw new Error(t("publish.errUnknownKind"));
  }
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-faint">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
